import {
  laboratories,
  LAB_SAMPLE_STATUSES,
  LAB_SAMPLE_STATUSES_DESC,
  ORDER_LOCK_TYPE,
} from '@/config/constants';

const insertAnalysesDataColumns = {
  stabilityToken: {
    key: 'stabilityTimepointToken',
    label: 'stabilityToken',
    type: 'text',
    sortable: 'custom',
    minWidth: 100,
  },
  stabilityTemperatureHumidity: {
    key: 'stabilityTemperature',
    label: 'stabilityTemperatureHumidity',
    type: 'function',
    sortable: false,
    minWidth: 130,
    get: (value, row) => {
      if (value == null) return '';
      const humidityPart =
        row.stabilityHumidity != null ? ` ${row.stabilityHumidity}%RH` : '';
      return `${value}°C${humidityPart}`;
    },
  },
  stabilityMatrix: {
    key: 'stabilityMatrix',
    label: 'stabilityMatrix',
    type: 'text',
    sortable: false,
    minWidth: 120,
  },
  groupingId: {
    key: 'groupingId',
    label: 'grouping',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  sampleId: {
    key: 'sampleId',
    label: 'customerSampleId',
    type: 'function',
    sortable: 'custom',
    minWidth: 150,
    get: (value, row) => (row.children ? '' : value),
  },
  id: {
    key: 'labSampleCode',
    label: 'sampleId',
    type: 'function',
    sortable: 'custom',
    minWidth: 150,
    get: (value, row) => (row.children ? '' : value),
  },
  orderId: {
    key: 'orderId',
    label: 'orderId',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  customerId: {
    key: 'customerProductName',
    label: 'customerSampleCode',
    type: 'text',
    sortable: 'custom',
    minWidth: 200,
  },
  status: {
    key: 'status',
    label: 'status',
    type: 'encoded',
    sortable: false,
    customClass: true,
    enum: 'sampleStatuses',
    decode: (value) => LAB_SAMPLE_STATUSES_DESC[value],
    getClassModifier: (value) => LAB_SAMPLE_STATUSES_DESC[value],
    minWidth: 130,
  },
  dilution: {
    key: 'dilution',
    label: 'dilution',
    type: 'enum',
    enum: 'dilutions',
    minWidth: 85,
    sortable: false,
  },
  finalCustomer: {
    key: 'finalCustomer',
    label: 'finalCustomer',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  finalCustomerRef: {
    key: 'finalCustomerRef',
    label: 'finalCustomerRef',
    type: 'text',
    sortable: false,
    minWidth: 130,
  },
  productName: {
    key: 'productName',
    label: 'productName',
    type: 'text',
    sortable: 'custom',
    minWidth: 150,
  },
  sampleDescription: {
    key: 'sampleDescription',
    label: 'sampleDescription',
    type: 'enum',
    enum: 'sampleDescriptions',
    sortable: 'custom',
    minWidth: 200,
  },
  lot: {
    key: 'lot',
    label: 'lot',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  lotDate: {
    key: 'date',
    label: 'lotDate',
    type: 'date',
    sortable: false,
    minWidth: 130,
  },
  date: {
    key: 'acceptedAt',
    label: 'receivedDate',
    type: 'date',
    sortable: 'custom',
    minWidth: 170,
  },
  quarantineDate: {
    key: 'quarantineDate',
    label: 'quarantineDate',
    type: 'date',
    sortable: 'custom',
    minWidth: 170,
  },
  startDate: {
    key: 'sampleAnalysisStart',
    label: 'sampleAnalysisStart',
    type: 'date',
    sortable: 'custom',
    minWidth: 130,
  },
  endDate: {
    key: 'sampleAnalysisEnd',
    label: 'sampleAnalysisEnd',
    type: 'date',
    sortable: 'custom',
    minWidth: 130,
  },
  sector: {
    key: 'sector',
    label: 'sector',
    type: 'enum',
    enum: 'sectors',
    sortable: 'custom',
    minWidth: 230,
  },
  showRawData: {
    key: 'showRawData',
    label: 'showRawData',
    type: 'boolean',
    align: 'center',
    sortable: false,
    minWidth: 110,
  },
  notes: {
    key: 'notes',
    label: 'notes',
    type: 'date',
    sortable: 'custom',
    minWidth: 130,
  },
  actions: {
    key: 'actions',
    label: 'actions',
    type: 'actions',
    sortable: false,
    fixed: true,
    minWidth: 200,
    buttons: [
      {
        name: 'proceedLocked',
        icon: 'fas el-icon-warning-outline',
        showCondition: (row) =>
          row.orderLocked === ORDER_LOCK_TYPE.ACCEPTANCE ||
          row.orderLocked === ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'reportLocked',
        icon: 'fas el-icon-warning',
        showCondition: (row) => row.orderLocked === ORDER_LOCK_TYPE.REPORT,
      },
      {
        name: 'viewAnalysesDetails',
        icon: 'fas fa-eye',
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'showComment',
        icon: 'fas fa-comment',
        showCondition: (row) => row.comments,
      },
      {
        name: 'insertExpectedValues',
        icon: 'fas fa-microscope',
        permissions: ['analysesDataManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'changeDilution',
        icon: 'fas fa-flask',
        permissions: ['analysesDataManage'],
        showCondition: (row) => row.laboratory === laboratories.MICR,
      },
      {
        name: 'askApproval',
        icon: 'fas fa-check-square',
        permissions: ['analysesDataManage'],
        showCondition: (row) => row.status === LAB_SAMPLE_STATUSES.TOBEVERIFIED,
      },
      {
        name: 'editSamplingInformation',
        icon: 'fas fa-tag',
        permissions: ['analysesDataManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'downloadAttachment',
        icon: 'fas fa-paperclip',
        showCondition: (row) => row.hasAttachments,
        permissions: ['receivedSamplesDownload', 'analysesDataManage'],
      },
    ],
  },
};

export default insertAnalysesDataColumns;
